// core/tools/pathUtils.ts — Pure utilities for path point ↔ SVG conversion
import type { Point } from './types';

export interface PathPoint {
  x: number;
  y: number;
  handleIn?: Point;   // control handle coming into this point (relative to point)
  handleOut?: Point;  // control handle going out of this point (relative to point)
}

/**
 * Convert PathPoint[] to an SVG path `d` attribute string.
 * Supports cubic bezier curves (C) and straight lines (L).
 */
export function pathPointsToSVG(points: PathPoint[], closed: boolean): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${fmt(points[0].x)},${fmt(points[0].y)}`;

  let d = `M${fmt(points[0].x)},${fmt(points[0].y)}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    d += segmentToSVG(prev, curr);
  }

  if (closed && points.length > 1) {
    d += segmentToSVG(points[points.length - 1], points[0]);
    d += 'Z';
  }

  return d;
}

/**
 * Generate the SVG segment between two points.
 * Uses cubic bezier (C) if either point has handles, otherwise line (L).
 */
function segmentToSVG(from: PathPoint, to: PathPoint): string {
  const hasHandles = from.handleOut || to.handleIn;

  if (hasHandles) {
    // Cubic bezier: C cp1x,cp1y cp2x,cp2y x,y
    const cp1x = from.x + (from.handleOut?.x ?? 0);
    const cp1y = from.y + (from.handleOut?.y ?? 0);
    const cp2x = to.x + (to.handleIn?.x ?? 0);
    const cp2y = to.y + (to.handleIn?.y ?? 0);
    return ` C${fmt(cp1x)},${fmt(cp1y)} ${fmt(cp2x)},${fmt(cp2y)} ${fmt(to.x)},${fmt(to.y)}`;
  }

  return ` L${fmt(to.x)},${fmt(to.y)}`;
}

/**
 * Calculate the bounding box of a set of path points (approximate — uses points + handles).
 */
export function pathPointsBounds(points: PathPoint[]): { x: number; y: number; width: number; height: number } {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const p of points) {
    // Include the point itself
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);

    // Include control handles (absolute positions)
    if (p.handleIn) {
      minX = Math.min(minX, p.x + p.handleIn.x);
      minY = Math.min(minY, p.y + p.handleIn.y);
      maxX = Math.max(maxX, p.x + p.handleIn.x);
      maxY = Math.max(maxY, p.y + p.handleIn.y);
    }
    if (p.handleOut) {
      minX = Math.min(minX, p.x + p.handleOut.x);
      minY = Math.min(minY, p.y + p.handleOut.y);
      maxX = Math.max(maxX, p.x + p.handleOut.x);
      maxY = Math.max(maxY, p.y + p.handleOut.y);
    }
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Format a number for SVG (max 2 decimal places) */
function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * Parse an SVG path `d` string into PathPoint[].
 * Supports M, L, C, and Z commands. Returns null if parsing fails.
 */
export function svgToPathPoints(d: string): { points: PathPoint[]; closed: boolean } | null {
  if (!d || !d.trim()) return null;

  const points: PathPoint[] = [];
  let closed = false;

  // Tokenize: split into commands + number sequences
  const tokens = d.match(/[MLCZmlcz]|[-+]?[0-9]*\.?[0-9]+/g);
  if (!tokens) return null;

  let i = 0;
  let currentX = 0;
  let currentY = 0;

  const num = (): number => {
    const val = parseFloat(tokens[i] ?? '0');
    i++;
    return val;
  };

  while (i < tokens.length) {
    const cmd = tokens[i];

    if (cmd === 'M' || cmd === 'm') {
      i++;
      const x = num();
      const y = num();
      currentX = cmd === 'M' ? x : currentX + x;
      currentY = cmd === 'M' ? y : currentY + y;
      points.push({ x: currentX, y: currentY });
    } else if (cmd === 'L' || cmd === 'l') {
      i++;
      const x = num();
      const y = num();
      currentX = cmd === 'L' ? x : currentX + x;
      currentY = cmd === 'L' ? y : currentY + y;
      points.push({ x: currentX, y: currentY });
    } else if (cmd === 'C' || cmd === 'c') {
      i++;
      const isRel = cmd === 'c';
      const cp1x = num() + (isRel ? currentX : 0);
      const cp1y = num() + (isRel ? currentY : 0);
      const cp2x = num() + (isRel ? currentX : 0);
      const cp2y = num() + (isRel ? currentY : 0);
      const x = num() + (isRel ? currentX : 0);
      const y = num() + (isRel ? currentY : 0);

      // handleOut on the previous point
      if (points.length > 0) {
        const prev = points[points.length - 1];
        prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
      }

      // handleIn on the new point
      const newPoint: PathPoint = {
        x, y,
        handleIn: { x: cp2x - x, y: cp2y - y },
      };
      points.push(newPoint);
      currentX = x;
      currentY = y;
    } else if (cmd === 'Z' || cmd === 'z') {
      i++;
      closed = true;
    } else {
      // Unknown or number — try to skip
      i++;
    }
  }

  return points.length > 0 ? { points, closed } : null;
}

/**
 * Insert a new point into a path at a given index.
 * The new point is placed at the midpoint of the segment.
 */
export function insertPointAtIndex(points: PathPoint[], afterIndex: number): PathPoint[] {
  if (afterIndex < 0 || afterIndex >= points.length - 1) return points;

  const a = points[afterIndex];
  const b = points[afterIndex + 1];

  // Simple midpoint (for lines). For curves, a more accurate subdivision would be needed.
  const mid: PathPoint = {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  };

  const result = [...points];
  result.splice(afterIndex + 1, 0, mid);

  // Clear handles on surrounding points for simplicity
  // (a proper implementation would subdivide the bezier)
  if (result[afterIndex].handleOut) result[afterIndex] = { ...result[afterIndex], handleOut: undefined };
  if (result[afterIndex + 2]?.handleIn) result[afterIndex + 2] = { ...result[afterIndex + 2], handleIn: undefined };

  return result;
}

/**
 * Remove a point from a path by index.
 */
export function removePointAtIndex(points: PathPoint[], index: number): PathPoint[] {
  if (index < 0 || index >= points.length) return points;
  const result = [...points];
  result.splice(index, 1);
  return result;
}
